import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Filter } from 'lucide-react';
import './CollectionSection.css';

// Constants
const PRICES = {
  smile: 5000000,
  sword: 10000000,
  paramecia: 150000000,
  zoan: 500000000,
  logia: 2000000000
};

const categories = [
  { id: 'paramecia', label: 'Paramecia' },
  { id: 'zoan', label: 'Zoan' },
  { id: 'logia', label: 'Logia' },
  { id: 'sword', label: 'Sword' },
  { id: 'smile', label: 'SMILE' },
];

const CollectionSection = ({refreshTokenIfNeeded, loggedInUser}) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [collectionItems, setCollectionItems] = useState([]); // Initial empty collection
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [isFilterPanelVisible, setFilterPanelVisible] = useState(true);

  useEffect(() => {
    const updateFilterPanelVisibility = () => {
      if (window.innerWidth < 1890) {
        setFilterPanelVisible(false); // Hide filter panel in portrait orientation
      } else {
        setFilterPanelVisible(true); // Show filter panel in landscape orientation
      }
    };
  
    // Set on initial render
    updateFilterPanelVisibility();
  
    // Optionally, add an event listener to handle window resize dynamically
    window.addEventListener('resize', updateFilterPanelVisibility);
  
    // Cleanup on component unmount
    return () => {
      window.removeEventListener('resize', updateFilterPanelVisibility);
    };
  }, []);  

  const toggleFilterPanel = () => {
    setFilterPanelVisible((prev) => !prev);
  };

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 1890) {
        setItemsPerPage(9); // Mobile (portrait orientation)
      } else {
        setItemsPerPage(30); // Desktop (landscape orientation)
      }
    };

    // Set on initial render
    updateItemsPerPage();

    // Update on resize
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const fetchCollection = async () => {
    try {
      const response = await axios.get('/api/collections/');
      const collectionData = response.data[0]; // Assuming only one collection object is returned
      setCollectionItems(collectionData.items); // Directly use the items from the API response
    } catch (error) {
      console.error('Error fetching collection data:', error);
      if (error.response && error.response.status === 401) {
        try {
          console.log('Refreshing token...');
          await refreshTokenIfNeeded();
  
          // Retry GET request
          console.log('Retrying collection request after token refresh...');
          const response = await axios.get('/api/collections/');
          const collectionData = response.data[0]; // Assuming only one collection object is returned
          setCollectionItems(collectionData.items); // Directly use the items from the API response 
        } catch (retryError) {
          console.error('Retry failed after token refresh:', retryError);
        }
      }
    }
  };

  useEffect(() => {
    if (loggedInUser && loggedInUser !== '') {
      fetchCollection();
    }
    else if (loggedInUser === '')
    {
      setCollectionItems([]);
    }
    else
    {
      // Do nothing
    }
  }, [loggedInUser]); 

  // Toggle category selection
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      }
      return [...prev, categoryId];
    });
    setCurrentPage(1); // Reset to first page when changing filters
  };

  // Filter and sort items based on selected categories
  const filteredItems = collectionItems
    .filter(item => selectedCategories.length === 0 || selectedCategories.includes(item.category))
    .sort((a, b) => categories.findIndex(c => c.id === a.category) - categories.findIndex(c => c.id === b.category));

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (delta) => setCurrentPage(prev => Math.max(1, Math.min(totalPages, prev + delta)));

  // Calculate total value of filtered items
  const totalValue = filteredItems.reduce((total, item) => total + (PRICES[item.category] || 0), 0);

  // Display empty message based on selected categories and filtered items
  const renderEmptyMessage = () => {
    if (collectionItems.length === 0) {
      return <div className="empty-message">You don't have any items.</div>;
    }
  
    if (selectedCategories.length === 1 && filteredItems.length === 0) {
      return <div className="empty-message">You don't have items in this category.</div>;
    }
  
    if (selectedCategories.length > 1 && filteredItems.length === 0) {
      return <div className="empty-message">You don't have items in these categories.</div>;
    }
  
    return null;
  };

  return (
    <div className="collection-section">
      <h1>My Collection</h1>
      <h3>Devil Fruits & Swords</h3>

      <div className="balance-display">
        <div className="balance-row">
          <span className="balance-label">Total Value</span>
          <span className="balance"><a href="https://onepiece.fandom.com/wiki/Belly" target="_blank" rel="noopener noreferrer">B${totalValue.toLocaleString()}</a></span>
        </div>
      </div>

      {isFilterPanelVisible && (
        <FilterPanel
          categories={categories}
          selectedCategories={selectedCategories}
          toggleCategory={toggleCategory}
        />
      )}

      <div className="collection-container">
        {filteredItems.length > 0 ? (
          <CollectionGrid items={currentItems} />
        ) : (
          renderEmptyMessage()
        )}
        {filteredItems.length > itemsPerPage && (
          <PaginationControls 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={handlePageChange} 
          />
        )}
      </div>

      <button
        className="toggle-filter-panel-btn"
        onClick={toggleFilterPanel}
      >
        <Filter size={24} strokeWidth={1.5} />
      </button>
    </div>
  );
};

// Filter Panel Component
const FilterPanel = ({ categories, selectedCategories, toggleCategory }) => (
  <div className="filter-panel">
    <div className="filter-title">Category</div>
    <div className="category-list">
      {categories.map(category => (
        <div 
          key={category.id}
          className={`category-item ${selectedCategories.includes(category.id) ? 'active' : ''}`}
          onClick={() => toggleCategory(category.id)}
        >
          <div className={`category-checkbox ${selectedCategories.includes(category.id) ? 'checked' : ''}`}>
            {selectedCategories.includes(category.id) && <Check size={14} color="white" />}
          </div>
          <span className="category-label">{category.label}</span>
        </div>
      ))}
    </div>
  </div>
);

// Collection Grid Component
const CollectionGrid = ({ items }) => (
  <div className="collection-grid">
    {items.map((item) => {
      // Generate the image URL dynamically based on the category
      const imageBaseUrl = `https://images.gomugomuguess.com/one-piece-collection-section-images/one-piece-${item.category}-images/`;
      const imageUrl = `${imageBaseUrl}${item.image_url}`;

      return (
        <div key={item.id} className="collection-item">
          <a href={`https://onepiece.fandom.com/wiki/${item.profile_suffix}`} target="_blank" rel="noopener noreferrer">
            <img src={imageUrl} alt={item.name} />
            <span className="collection-item-title">{item.name}</span>
          </a>
        </div>
      );
    })}
  </div>
);

// Pagination Controls Component
const PaginationControls = ({ currentPage, totalPages, onPageChange }) => (
  <div className="pagination-controls">
    <button 
      onClick={() => onPageChange(-1)} 
      disabled={currentPage === 1} 
      className="pagination-button"
    >
      &lt;
    </button>
    <span className="pagination-text">Page {currentPage}</span>
    <button 
      onClick={() => onPageChange(1)} 
      disabled={currentPage === totalPages} 
      className="pagination-button"
    >
      &gt;
    </button>
  </div>
);

export default CollectionSection;
